// components/CompCard.jsx - Updated with Live Affiliates Navigation and Traffic Fix
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSelectedBrand } from '../../store/slices/liveAffiliatesSlice';
import { Globe, ExternalLink, Zap, Activity, Users, TrendingUp, TrendingDown } from 'lucide-react';
import CompFaviconImage from '../common/FaviconImage';

const CompCard = ({ brand, onStatusUpdate, processingInfo }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('1');
  const [currentStep, setCurrentStep] = useState(1);
  const [stepDescription, setStepDescription] = useState('Initializing brand analysis...');

  // Processing steps (fallback if not using Redux processingInfo)
  // Backend has 8 steps (1-8) then 'completed'
  const processingSteps = [
    { step: 1, label: 'Brand Setup', description: 'Starting analysis...' },
    { step: 2, label: 'Data Fetch', description: 'Fetching backlinks...' },
    { step: 3, label: 'Searching Traffic', description: 'Searching for affiliates...' },
    { step: 4, label: 'Classification', description: 'Classifying websites...' },
    { step: 5, label: 'Processing Data', description: 'Processing backlink data...' },
    { step: 6, label: 'Finding Keywords', description: 'Analyzing organic keywords...' },
    { step: 7, label: 'Analyzing Data', description: 'Analyzing paid keywords...' },
    { step: 8, label: 'Finalizing', description: 'Finalizing results...' }
  ];

  // Check if brand is processing - FIXED LOGIC
  useEffect(() => {
    // Get status from brand data
    const brandStatus = brand.processing_status || brand.processingStatus;
    const brandStatusString = String(brandStatus || '');
    const brandStatusNumber = parseInt(brandStatus) || 0;

    // Determine if processing based on brand status (1-8 are processing states)
    const isBrandProcessing = brandStatus &&
      brandStatusString !== 'completed' &&
      brandStatusString !== '0' &&
      brandStatusString !== 'error' &&
      brandStatusString !== 'failed' &&
      brandStatusNumber >= 1 &&
      brandStatusNumber <= 8;


    // If we have ACTIVE processingInfo from Redux store, use that (most up-to-date)
    if (processingInfo && processingInfo.isActive) {
      setIsProcessing(true);
      setProcessingStatus(processingInfo.status || '1');
      setStepDescription(processingInfo.step || 'Processing...');

      const statusNumber = parseInt(processingInfo.status) || 1;
      setCurrentStep(Math.min(Math.max(statusNumber, 1), 8));
      return;
    }

    // Check if brand is processing based on database status (1-8)
    if (isBrandProcessing) {
      setIsProcessing(true);
      setProcessingStatus(brandStatus);

      const stepNumber = Math.min(Math.max(brandStatusNumber, 1), 8);
      setCurrentStep(stepNumber);
      const stepInfo = processingSteps.find(s => s.step === stepNumber);
      setStepDescription(stepInfo?.description || 'Processing...');
      return;
    }

    // Not processing
    setIsProcessing(false);
    if (brandStatusString === 'completed') {
      setCurrentStep(8);
      setStepDescription('Analysis complete!');
    } else if (brandStatusString === 'error' || brandStatusString === 'failed') {
      setStepDescription('Processing failed');
    }
  }, [brand.processing_status, brand.processingStatus, processingInfo]);

  // Polling effect for this specific brand (only if not using Redux processingInfo)
  useEffect(() => {
    let pollingInterval;

    // Skip local polling if Redux is handling it via processingInfo
    if (processingInfo) {
      return;
    }

    if (isProcessing && brand.id) {
      pollingInterval = setInterval(async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/competitiveNew/status/${brand.id}`,
            {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
            }
          );

          if (response.ok) {
            const result = await response.json();

            if (result.success) {
              const newStatus = result.processingStatus;
              const statusString = String(newStatus);
              const statusNumber = parseInt(newStatus) || 0;

              // Check if processing is completed (status 'completed' or '0')
              const isCompleted = result.isCompleted ||
                statusString === 'completed' ||
                statusString === '0' ||
                statusNumber === 0;

              setProcessingStatus(newStatus);

              if (!isCompleted) {
                const stepNumber = Math.min(Math.max(statusNumber, 1), 8);
                setCurrentStep(stepNumber);
                const stepInfo = processingSteps.find(s => s.step === stepNumber);
                setStepDescription(stepInfo?.description || 'Processing...');

              } else {
                setIsProcessing(false);
                setCurrentStep(8);
                setStepDescription('Analysis complete!');


                // Notify parent component to refresh brands
                if (onStatusUpdate) {
                  onStatusUpdate(brand.id, 'completed');
                }
              }
            }
          } else {
            console.error(`❌ Polling failed for brand ${brand.id}: ${response.status}`);
          }
        } catch (error) {
          console.error('Polling error for brand:', brand.id, error);
        }
      }, 5000); // Poll every 5 seconds
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [isProcessing, brand.id, onStatusUpdate, processingInfo]);

  // Live Affiliates click handler
  // Allow clicking even during processing so users can see data as it becomes available
  const handleLiveAffiliatesClick = () => {
    dispatch(setSelectedBrand({
      brand_url: brand.brand_url || brand.url,
      brand_name: brand.brand_name || brand.name,
      brand_id: brand.id,
      country: brand.country,
      vertical: brand.vertical
    }));
    navigate('/live-affiliate');
  };

  const hasCurrentMonthData = brand.current_month_gained !== undefined || brand.current_month_lost !== undefined;
  const gainedCount = brand.current_month_gained || 0;
  const lostCount = brand.current_month_lost || 0;

  // Show actual values even during processing so users can see data as it becomes available
  const metrics = [
    {
      title: 'MARS Health Score',
      value: brand.mars_score || brand.mars_health_score || (isProcessing ? '...' : 'N/A'),
      icon: Zap,
      isProcessing: isProcessing,
      onClick: null
    },
    {
      title: 'Estimated Affiliate Traffic',
      value: brand.estimated_affiliate_traffic || brand.total_traffic || (isProcessing ? '...' : 'N/A'),
      icon: Activity,
      isProcessing: isProcessing,
      onClick: null
    },
    {
      title: 'Live Affiliates',
      value: (brand.gained || 0) + (brand.lost || 0) || brand.live_affiliates_count || 0,
      icon: Users,
      isProcessing: isProcessing,
      onClick: handleLiveAffiliatesClick
    }
  ];

  const handleExternalLinkClick = (e) => {
    e.stopPropagation();
    window.open(`https://${brand.brand_url}`, '_blank');
  };

  // Use processingInfo progress if available, otherwise calculate from steps (8 total)
  const progressPercentage = processingInfo?.progress ?? (currentStep / 8) * 100;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 transition-all duration-300 group relative overflow-hidden transform hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-sm hover:-translate-y-0.5">

      <div className="relative p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="relative flex items-center gap-2">
              <div className="w-7 h-7 rounded-md flex items-center justify-center overflow-hidden">
                <CompFaviconImage
                  domain={brand.brand_url}
                  faviconUrl={brand.favicon_url}
                  size={28}
                  className="rounded-md"
                />
              </div>
            </div>
            <div>
              <h3 className="text-base font-medium text-blue-600 dark:text-blue-400 mb-0.5">
                {brand.brand_name}
              </h3>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400">
                  <Globe className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  <span>{brand.brand_url}</span>
                  <ExternalLink
                    className="w-3 h-3 cursor-pointer text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                    onClick={handleExternalLinkClick}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Processing Progress Bar */}
        {isProcessing && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-slate-400">
                {stepDescription}
              </span>
              <span className="text-xs text-gray-400 dark:text-slate-500 bg-gray-100 dark:bg-slate-800 px-2 py-1 rounded-full">
                Step {currentStep}/8
              </span>
            </div>

            {/* Progress Bar */}
            <div className="relative">
              <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-blue-500 dark:bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-xs text-gray-400 dark:text-slate-500">{Math.round(progressPercentage)}%</span>
              </div>
            </div>
          </div>
        )}

        <div className="h-px bg-gray-200 dark:bg-slate-700 mb-4"></div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {metrics.map((metric, metricIndex) => (
            <div
              key={metricIndex}
              className={`border rounded-lg p-3 transition-all duration-300 transform hover:scale-105 hover:shadow-sm ${
                metric.isProcessing
                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                  : 'bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700'
              } ${
                metric.onClick
                  ? 'cursor-pointer'
                  : ''
              }`}
              onClick={metric.onClick}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <div className="flex items-center gap-2">
                  <metric.icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className={`text-sm font-medium ${
                      metric.isProcessing ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-slate-100'
                    }`}>
                      {metric.value}
                    </div>

                    {/* Gained/Lost arrows for Live Affiliates Count */}
                    {metric.title === 'Live Affiliates' && hasCurrentMonthData && (
                      <div className="flex items-center gap-1">
                        {gainedCount > 0 && (
                          <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 rounded text-xs">
                            <TrendingUp className="w-3 h-3 text-green-600 dark:text-green-400" />
                            <span className="text-green-700 dark:text-green-300 font-medium">{gainedCount}</span>
                          </div>
                        )}
                        {lostCount > 0 && (
                          <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 rounded text-xs">
                            <TrendingDown className="w-3 h-3 text-red-600 dark:text-red-400" />
                            <span className="text-red-700 dark:text-red-300 font-medium">{lostCount}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className={`text-xs font-medium ${
                metric.isProcessing ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-slate-400'
              }`}>
                {metric.title}
              </div>
            </div>
          ))}
        </div>

        {/* Processing completed indicator */}
        {/*!isProcessing && (processingStatus === 'completed' || processingStatus === '0' || processingStatus === 0 || processingStatus === '8' || processingStatus === 8) && (
          <div className="mt-3 flex items-center gap-2 text-green-600 text-sm">
            <CheckCircle className="w-4 h-4" />
            <span>Analysis completed successfully</span>
          </div>
        )*/}
      </div>
    </div>
  );
};

export default CompCard;